export interface BundleProduct {
  product_id: string;
  product_sku: string;
  product_name: string;
  /** Per-set quantity (`bundle_per_set_qty`). */
  quantity: number;
  inventory_type_id?: string | null;
}

export interface Bundle {
  id: string;
  sku: string;
  name: string;
  bundle_products?: BundleProduct[] | null;
}

export interface BundleGroupEntry {
  bundle: Bundle;
  setQuantity: number;
  perSetQuantities: Record<string, number>;
}

export interface InventoryType {
  id: string;
  name: string;
}

interface SelectOption {
  value: string;
  label: string;
}

export interface BundleExpandedItem {
  key: string | number;
  product_id: SelectOption;
  product_sku: string;
  product_name: string;
  product_validity_days: number;
  manufacturing_date_required: boolean;
  batch_management_required: boolean;
  inventory_type_id: SelectOption | null;
  quantity: number;
  expiration_date: null;
  manufacturing_date: null;
  batch: string;
  note: string;
  price: number | null;
  identifier: string;
  original_price: number | null;
  cost: number | null;
  custom_attributes: Record<string, unknown>;
  bundle_id: string;
  bundle_key: string;
  bundle_per_set_qty: number;
}

export interface ExpandBundleOptions {
  /** Override the per-item key generator. Defaults to `Date.now() + Math.random()`. */
  makeKey?: () => string | number;
  /** Initial set quantity for the bundle (default 1). */
  initialSetQuantity?: number;
}

export interface ExpandBundleResult {
  items: BundleExpandedItem[];
  perSetQuantities: Record<string, number>;
}

function resolveInventoryType(
  bp: BundleProduct,
  inventoryTypes: readonly InventoryType[],
): SelectOption | null {
  if (bp.inventory_type_id) {
    const match = inventoryTypes.find(t => t.id === bp.inventory_type_id);
    if (match) return { value: match.id, label: match.name };
  }
  const fallback = inventoryTypes[0];
  return fallback ? { value: fallback.id, label: fallback.name } : null;
}

/**
 * Expand a bundle definition into the per-product form items that the order
 * form actually stores. Each child gets a `bundle_key` so subsequent recalc /
 * remove operations can target the whole group.
 *
 * `setQuantity` defaults to 1: a freshly expanded bundle always represents
 * "1 set". `quantity = bundle_per_set_qty * setQuantity` follows trivially.
 */
export function expandBundleToItems(
  bundle: Bundle,
  bundleKey: string,
  inventoryTypes: readonly InventoryType[],
  options: ExpandBundleOptions = {},
): ExpandBundleResult {
  const makeKey = options.makeKey ?? (() => Date.now() + Math.random());
  const setQuantity = options.initialSetQuantity ?? 1;

  const perSetQuantities: Record<string, number> = {};
  const items: BundleExpandedItem[] = [];

  for (const bp of bundle.bundle_products || []) {
    perSetQuantities[bp.product_id] = bp.quantity;
    items.push({
      key: makeKey(),
      product_id: { value: bp.product_id, label: `${bp.product_sku} - ${bp.product_name}` },
      product_sku: bp.product_sku,
      product_name: bp.product_name,
      product_validity_days: 0,
      manufacturing_date_required: false,
      batch_management_required: false,
      inventory_type_id: resolveInventoryType(bp, inventoryTypes),
      quantity: bp.quantity * setQuantity,
      expiration_date: null,
      manufacturing_date: null,
      batch: '',
      note: '',
      price: null,
      identifier: '',
      original_price: null,
      cost: null,
      custom_attributes: {},
      bundle_id: bundle.id,
      bundle_key: bundleKey,
      bundle_per_set_qty: bp.quantity,
    });
  }

  return { items, perSetQuantities };
}

interface BundleAwareItem {
  bundle_key?: string | null;
  bundle_per_set_qty?: number | null;
  quantity?: number | null;
  [extra: string]: unknown;
}

/**
 * Recompute `quantity` for every item that belongs to `bundleKey` based on the
 * new set quantity. Items in other bundles (or non-bundle items) are returned
 * untouched (same reference). Caller is responsible for setting the new array
 * back on the form.
 */
export function recalcBundleItemQuantities<T extends BundleAwareItem>(
  items: readonly T[],
  bundleKey: string,
  newSetQuantity: number,
): T[] {
  return items.map(item => {
    if (item?.bundle_key === bundleKey) {
      return { ...item, quantity: (item.bundle_per_set_qty || 1) * newSetQuantity };
    }
    return item;
  });
}

/**
 * Indices of items belonging to `bundleKey`, returned in REVERSE order so the
 * caller can `splice` / `removeAt` from the back without index drift.
 */
export function findBundleItemIndices(
  items: readonly BundleAwareItem[],
  bundleKey: string,
): number[] {
  const indices: number[] = [];
  items.forEach((item, idx) => {
    if (item?.bundle_key === bundleKey) indices.push(idx);
  });
  indices.reverse();
  return indices;
}
